<script lang="ts" setup>
import type { FocusTrap } from 'focus-trap'
import { faClose } from '@fortawesome/pro-regular-svg-icons'
import { useEventListener, useTimeoutFn } from '@vueuse/core'
import { preventWindowScroll } from '@shared/composables'
import GIconButton from '@consumer/components/GIconButton.vue'
import ConciergeRecommendations
  from '@consumer/modules/myAccount/shared/recommendations/ConciergeRecommendations.vue'
import ConciergeInputVariant, { type ConciergeInputVariantName } from './ConciergeInputVariant.vue'
import { provideConciergeTakeover } from './useConciergeTakeover'
import { trackConciergeEvent } from './conciergeEventTracking'
import { useConciergeFunnelTracking } from './useConciergeFunnelTracking'

// The input lives inline on the homepage. It expands into a full-screen takeover
// when the buyer is focused in a field that has enough to fetch results — the SAME
// input teleports across (no re-mount, no lost keystrokes). Engagement is driven by
// focus + resultability rather than typing, so refocusing an already-full field
// re-expands without a keystroke. Once open, editing the field down (even to empty)
// keeps it open — the buyer is most likely retyping; only an explicit dismiss or a
// click outside the field while it's empty drops back to the homepage.
// Which input UI to render. Defaults to the free-text variant so nothing changes
// unless a variant is explicitly threaded in (query-param override today, split
// assignment once the UI experiment is registered). Both variants write the same
// store, so everything below here — engagement, teleport, results — is identical.
withDefaults(defineProps<{
  variant?: ConciergeInputVariantName
}>(), { variant: 'free_text', })

const takeover = provideConciergeTakeover()

useConciergeFunnelTracking(() => __props.variant)

const panel = ref<HTMLElement | null>(null)
const inner = ref<HTMLElement | null>(null)

const engaged = ref(false)
// True only for the tick an explicit dismiss (✕/Escape) is collapsing, so the focus
// handler drops focus instead of restoring it — restoring would immediately re-open
// a still-ready field.
let dismissing = false

// Open while the buyer is focused in a resultable field. Editing it down — even to
// empty — never closes it: being kicked back to the homepage mid-edit is jarring when
// the buyer is about to retype. Only an explicit dismiss or a click outside the field
// while it's empty closes it (see collapse / onSurfaceClick). Focus leaving doesn't
// close it either — the teleport briefly blurs the input mid-move, and closing on that
// would bounce the takeover straight shut.
//
// Deferred a task, not a tick: a click focuses the input BEFORE the browser places the
// caret where it landed, and Vue flushes in between. Engaging in that gap teleports the
// input away and restores a caret the click hadn't set yet (it reads as 0 — the start of
// the text), so let the click finish first, then re-check.
const { start: scheduleEngage } = useTimeoutFn(() => {
  if (takeover.shouldEngage.value) engaged.value = true
}, 0, { immediate: false })

watch(takeover.shouldEngage, (should) => {
  if (should) scheduleEngage()
})

function collapse () {
  // No-op when already collapsed: otherwise a stray Escape sets `dismissing` without
  // the engaged watcher ever running to clear it, and it would leak into the next
  // readiness-collapse (blurring instead of restoring caret).
  if (!engaged.value) return
  dismissing = true
  engaged.value = false
}

// Escape closes from anywhere, not only from inside the input: a backdrop click while the
// query still has text keeps the takeover open but blurs the input, and the next Escape
// then fires on <body>, where a handler on the surface never hears it. Escape inside some
// other open dialog is left to that dialog.
useEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return
  const target = event.target
  if (target instanceof Node && target !== document.body && !panel.value?.contains(target)) return
  collapse()
})

// While the field is empty, a click anywhere off it means "I'm done" — collapse. A
// click on the field itself (to retype), or any click while there's still text, does
// not: an empty takeover has nothing to keep and nothing to browse.
function onSurfaceClick (event: MouseEvent) {
  if (!engaged.value || !takeover.dismissOnOutsideClick.value) return
  if (event.target instanceof Element && event.target.closest('input')) return
  collapse()
}

// Load focus-trap lazily: it (and tabbable) are only needed once the takeover opens,
// so keeping them out of the statically-bundled island leaves the landing-page payload
// unchanged at rest — and for all of control while the experiment sits at weight 0.
let trap: FocusTrap | null = null
async function activateTrap () {
  if (!panel.value) return
  if (!trap) {
    const { createFocusTrap } = await import('focus-trap')
    if (!panel.value) return
    // We drive focus/blur ourselves (see the engaged watch); the trap must not set
    // initial focus or return focus on deactivate, or dismissing a still-ready field
    // would re-focus the input and immediately re-open the takeover.
    trap = createFocusTrap(panel.value, {
      initialFocus: false,
      escapeDeactivates: false,
      returnFocusOnDeactivate: false,
      allowOutsideClick: true,
    })
  }
  trap.activate()
}

// iOS-safe scroll lock, reactive to `engaged` (the repo primitive GModal et al. use;
// it tears itself down on unmount).
preventWindowScroll({ when: () => engaged.value })

// The input the buyer is actually in — captured generically rather than by id,
// because the madlibs variant is four inputs and the free-text variant one. The
// teleport moves nodes without re-mounting them, so the reference stays valid on
// the other side.
function focusedInput (): HTMLInputElement | null {
  const active = document.activeElement
  return active instanceof HTMLInputElement && panel.value?.contains(active) ? active : null
}

const MOVE_MS = 380

// True while the content glides home after a collapse. The surface is back in the page by
// then, at its resting paint order, where the hero (a Swiper, which layers itself up)
// draws over it — so the glide keeps the dialog layer until it lands.
const collapsing = ref(false)

function prefersReducedMotion (): boolean {
  return typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

// FLIP between the content's two homes: `first` is its rect before the teleport,
// measured pre-patch, and the glide runs from there to wherever it just landed — up
// from the homepage slot into the takeover on open, back into the slot on collapse.
// The white backdrop fades on its own (see the template), in step with the glide.
function animateMove (first: DOMRect): Animation | null {
  const content = inner.value
  if (!content || typeof content.animate !== 'function' || prefersReducedMotion()) return null

  const last = content.getBoundingClientRect()
  const dx = first.left - last.left
  const dy = first.top - last.top
  if (!dx && !dy) return null
  return content.animate(
    [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }],
    { duration: MOVE_MS, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
  )
}

// Teleporting re-parents the input, which blurs it. On open and on a readiness-driven
// collapse, restore focus + the buyer's exact caret to the SAME field they were in, so
// clicking a blank on the homepage lands them typing in that blank inside the takeover.
// On an explicit dismiss, drop focus so a still-ready field doesn't immediately re-open.
watch(engaged, async (open) => {
  if (open)
    trackConciergeEvent('takeover_engage', { variant: __props.variant })
  else
    trackConciergeEvent('takeover_collapse', { variant: __props.variant, metadata: { method: dismissing ? 'dismiss' : 'readiness' } })

  const input = focusedInput()
  const start = input?.selectionStart ?? null
  const end = input?.selectionEnd ?? null
  const firstRect = inner.value?.getBoundingClientRect() ?? null

  await nextTick()

  if (open) {
    // Restore focus + caret before awaiting the lazy trap, so the teleport doesn't
    // leave the input blurred while the focus-trap chunk loads on first open.
    if (input) {
      input.focus()
      if (start !== null && end !== null) input.setSelectionRange(start, end)
    }
    if (firstRect) animateMove(firstRect)
    await activateTrap()
    return
  }

  trap?.deactivate()
  const wasDismiss = dismissing
  dismissing = false
  if (wasDismiss) {
    if (input && document.activeElement === input) input.blur()
  }
  else if (input) {
    input.focus()
    if (start !== null && end !== null) input.setSelectionRange(start, end)
  }
  const glide = firstRect ? animateMove(firstRect) : null
  if (glide) {
    collapsing.value = true
    glide.onfinish = glide.oncancel = () => { collapsing.value = false }
  }
})

onBeforeUnmount(() => trap?.deactivate())
</script>

<template>
  <div class="concierge-host">
    <Transition name="concierge-backdrop">
      <div v-if="engaged" class="concierge-backdrop z-dialog"/>
    </Transition>

    <Teleport to="body" :disabled="!engaged">
      <div
        ref="panel"
        class="concierge-surface"
        :class="[
          { 'concierge-surface--engaged': engaged, 'concierge-surface--collapsing': collapsing },
          engaged || collapsing ? 'z-dialog' : '',
        ]"
        :role="engaged ? 'dialog' : undefined"
        :aria-modal="engaged ? 'true' : undefined"
        :aria-label="engaged ? 'Gift concierge' : undefined"
        @click="onSurfaceClick"
      >
        <GIconButton
          v-if="engaged"
          class="concierge-surface__close"
          :icon="faClose"
          size="medium"
          label="Close gift concierge"
          name="close-buyers-concierge"
          @click="collapse"
        />

        <div ref="inner" class="concierge-surface__inner">
          <ConciergeInputVariant :variant="variant"/>

          <ConciergeRecommendations :variant="variant"/>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style lang="scss" scoped>
.concierge-host {
  width: 100%;
}

// At rest the surface is a transparent block: ConciergeInput renders exactly where
// the page places it, no extra chrome. Capped at the landing container's lg width
// (1240px) rather than an input-sized column: the results sections run as wide as
// the rest of the page's content, and the inputs cap themselves at their own
// comfortable column width.
.concierge-surface__inner {
  margin: 0 auto;
  max-width: 1240px;
  width: 100%;
}

// Engaged: full-screen takeover covering everything, the same input now with room
// below for results. The white is `.concierge-backdrop`, a separate fixed layer, so it
// can still be fading out after this surface has returned to the page on collapse.
// z-layering comes from the z-dialog utility.
.concierge-surface--engaged {
  inset: 0;
  overflow-y: auto;
  padding: 64px 20px 48px;
  position: fixed;

  .concierge-surface__inner {
    padding-top: 4vh;
  }
}

// Positioned so the z-dialog utility applies for the collapse glide (see `collapsing`);
// at rest the surface is a plain block again.
.concierge-surface--collapsing {
  position: relative;
}

// Fades in under the entry glide and out over the collapse glide, so the page fades
// back in as the input returns to its slot instead of snapping.
.concierge-backdrop {
  background: var(--color-bg-surface-white);
  inset: 0;
  position: fixed;
}

.concierge-backdrop-enter-active {
  transition: opacity 0.24s ease-out;
}

.concierge-backdrop-leave-active {
  transition: opacity 0.38s ease-out;
}

.concierge-backdrop-enter-from,
.concierge-backdrop-leave-to {
  opacity: 0;
}

// Pinned to the viewport so it stays reachable while the surface scrolls. A sibling of
// `__inner`, not a child: the glide translates `__inner`, and a transformed ancestor
// would pin the button to that column instead — it would ride the glide, then jump to
// the corner once the transform cleared. Fades in alongside the glide instead of popping.
.concierge-surface__close {
  animation: concierge-close-fade 0.38s ease-out;
  position: fixed;
  right: 20px;
  top: 20px;
}

@keyframes concierge-close-fade {
  from {
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .concierge-surface__close {
    animation: none;
  }

  .concierge-backdrop-enter-active,
  .concierge-backdrop-leave-active {
    transition: none;
  }
}

@media (min-width: 1024px) {
  .concierge-surface--engaged .concierge-surface__inner {
    // Sit the input in the upper third rather than dead-center, leaving the lower
    // two-thirds as the results canvas.
    padding-top: 12vh;
  }
}
</style>
